import { ArrowLeft, Loader2, XOctagon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
	useAbortOnboarding,
	useOnboardingRun,
	useRetryOnboardingStep,
	useStartOnboarding,
} from "@/api/hooks";
import { useOnboardingStream } from "@/api/useOnboardingStream";
import { LogViewer } from "@/components/onboarding/LogViewer";
import { StepTracker } from "@/components/onboarding/StepTracker";
import { WelcomeEmailPanel } from "@/components/onboarding/WelcomeEmailPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/** Full-screen detail page with live progress. On mount, if the run
 * is ``pending``, auto-invokes ``start`` so the wizard flow is
 * single-page: create → navigate → auto-start → watch. */
export function OnboardingDetailPage() {
	const { id = "" } = useParams<{ id: string }>();
	const runQuery = useOnboardingRun(id);
	const start = useStartOnboarding(id);
	const retry = useRetryOnboardingStep(id);
	const abort = useAbortOnboarding(id);
	const stream = useOnboardingStream(id);
	const [expanded, setExpanded] = useState<number | null>(null);
	const [autoStarted, setAutoStarted] = useState(false);

	// Auto-start a freshly-created pending run.
	useEffect(() => {
		if (!runQuery.data || autoStarted) return;
		if (runQuery.data.status === "pending") {
			setAutoStarted(true);
			start.mutate();
		}
	}, [runQuery.data, autoStarted, start]);

	// Poll the run on status transitions so payload + timestamps update.
	const liveRunStatus = stream.runStatus ?? runQuery.data?.status ?? null;
	useEffect(() => {
		if (!liveRunStatus) return;
		if (["succeeded", "failed", "aborted"].includes(liveRunStatus)) {
			void runQuery.refetch();
		}
	}, [liveRunStatus, runQuery.refetch]);

	const isTerminal = useMemo(
		() =>
			liveRunStatus === "succeeded" || liveRunStatus === "failed" || liveRunStatus === "aborted",
		[liveRunStatus],
	);

	const handleAbort = async () => {
		if (!window.confirm("Abort this run? Provisioned resources are NOT torn down.")) {
			return;
		}
		try {
			await abort.mutateAsync();
		} catch {
			/* surfaced via abort.error */
		}
	};

	if (runQuery.isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<p className="text-sm text-muted-foreground">Loading run…</p>
			</div>
		);
	}
	if (runQuery.error) {
		const msg = runQuery.error instanceof ApiError ? runQuery.error.detail : "Load failed.";
		return (
			<div className="flex min-h-screen items-center justify-center">
				<p className="text-sm text-destructive">{msg}</p>
			</div>
		);
	}

	const run = runQuery.data;
	if (!run) return null;

	const expandedLogs = expanded != null ? (stream.logsByStep[expanded] ?? []) : [];

	return (
		<div className="min-h-screen bg-muted/20">
			<header className="border-b bg-background">
				<div className="mx-auto flex max-w-4xl items-center justify-between p-4">
					<Button variant="ghost" size="sm" asChild>
						<Link to="/">
							<ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
							Dashboard
						</Link>
					</Button>
					<h1 className="text-sm font-medium text-muted-foreground">
						Onboarding — {run.display_name}
					</h1>
					<span className="w-24" />
				</div>
			</header>
			<main className="mx-auto max-w-4xl space-y-6 p-6">
				<RunHeader run={run} liveStatus={liveRunStatus} connected={stream.connected} />

				{liveRunStatus === "failed" && run.error_message ? (
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2 text-destructive">
								<XOctagon className="h-4 w-4" aria-hidden />
								Run failed
							</CardTitle>
							<CardDescription>{run.error_message}</CardDescription>
						</CardHeader>
					</Card>
				) : null}

				<Card>
					<CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
						<div>
							<CardTitle className="text-lg">Steps</CardTitle>
							<CardDescription>
								Click a row to show metadata. Click Retry on a failed step to resume.
							</CardDescription>
						</div>
						{!isTerminal ? (
							<Button variant="outline" size="sm" onClick={handleAbort} disabled={abort.isPending}>
								Abort run
							</Button>
						) : null}
					</CardHeader>
					<CardContent>
						<StepTracker
							steps={run.steps}
							liveStatuses={stream.stepStatuses}
							liveErrors={stream.stepErrors}
							expandedOrdinal={expanded}
							onToggleExpand={(o) => setExpanded((prev) => (prev === o ? null : o))}
							onRetry={(o) => retry.mutate(o)}
							retryPending={retry.isPending}
						/>
					</CardContent>
				</Card>

				{expanded != null ? (
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Step {expanded} · live logs</CardTitle>
							<CardDescription>
								Latest chunks streamed via SSE. For older output use the paginated{" "}
								<span className="font-mono">/steps/{expanded}/logs</span>.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<LogViewer lines={expandedLogs} />
						</CardContent>
					</Card>
				) : null}

				{liveRunStatus === "succeeded" ? <WelcomeEmailPanel run={run} /> : null}
			</main>
		</div>
	);
}

function RunHeader({
	run,
	liveStatus,
	connected,
}: {
	run: { slug: string; display_name: string; portal_url: string | null };
	liveStatus: string | null;
	connected: boolean;
}) {
	return (
		<Card>
			<CardHeader>
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="space-y-1">
						<CardTitle className="text-xl">{run.display_name}</CardTitle>
						<CardDescription>
							<span className="font-mono text-xs">{run.slug}</span>
							{run.portal_url ? (
								<>
									{" · "}
									<a
										href={run.portal_url}
										target="_blank"
										rel="noopener noreferrer"
										className="underline decoration-dotted"
									>
										{run.portal_url}
									</a>
								</>
							) : null}
						</CardDescription>
					</div>
					<div className="flex items-center gap-2">
						{liveStatus === "running" ? (
							<Badge variant="outline">
								<Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
								Running
							</Badge>
						) : liveStatus === "succeeded" ? (
							<Badge variant="success">Succeeded</Badge>
						) : liveStatus === "failed" ? (
							<Badge variant="destructive">Failed</Badge>
						) : liveStatus === "aborted" ? (
							<Badge variant="outline" className="text-muted-foreground">
								Aborted
							</Badge>
						) : (
							<Badge variant="outline" className="text-muted-foreground">
								Pending
							</Badge>
						)}
						<span
							className="text-xs text-muted-foreground"
							title={connected ? "SSE connected" : "SSE disconnected"}
						>
							{connected ? "● live" : "○ offline"}
						</span>
					</div>
				</div>
			</CardHeader>
		</Card>
	);
}
